import React, { useEffect, useMemo, useState } from 'react'
import { Routes, Route, Outlet, useLocation, useNavigate, useOutletContext, useParams, Link, Navigate } from 'react-router-dom'
import SEO from '../components/SEO'
import { PlayCircle, MonitorPlay, Presentation, ArrowRight, Wrench, BookOpen, Video, ChevronDown, ChevronRight, Fish } from 'lucide-react'
import GuideManual from './GuideManual'
import { GUIDE_MANUAL_GROUPS } from './GuideManualData'
import GuideShrimp from './GuideShrimp'
import { GUIDE_SHRIMP_PAGES } from './GuideShrimpData'

const VIDEO_TUTORIALS = [
  {
    id: 0,
    title: '如何使用 DClaw 技能插件',
    description: '了解并掌握 DClaw 强大的技能生态，让你的 AI 助手具备无限可能。',
    icon: Wrench,
    videoUrl: 'https://www.dianclaw.cn/uploads/videos/skills.mp4',
    steps: [
      '打开 DClaw 客户端，进入技能中心或直接在对话框输入 "/" 唤出技能列表。',
      '浏览并选择你需要使用的技能插件（如：文档分析、数据处理等）。',
      '根据技能提示，输入或上传相应的内容素材。',
      '等待 AI 调用特定技能，为你返回高度专业化的处理结果。'
    ]
  },
  {
    id: 1,
    title: '用 DClaw 快速制作视频',
    description: '学习如何使用 DClaw 客户端在几分钟内生成高质量的视频内容。',
    icon: PlayCircle,
    videoUrl: 'https://www.dianclaw.cn/uploads/videos/CreateVideo.mp4',
    steps: ['打开 DClaw 客户端，进入对话或创作界面。', '输入你想生成视频的描述提示词。', '选择视频比例和风格参数。', '点击发送，等待大模型为您生成专属视频。']
  },
  {
    id: 2,
    title: '用 DClaw 快速制作网站',
    description: '无需写代码，让 DClaw 帮你从零开始构建一个漂亮的响应式网站。',
    icon: MonitorPlay,
    videoUrl: 'https://www.dianclaw.cn/uploads/videos/CreateWebSite.mp4',
    steps: ['在对话框中输入你想要的网站主题和设计要求。', 'DClaw 将自动生成前端代码并实时预览。', '你可以提出修改意见，AI 会自动帮你调整网页细节。', '完成设计后，一键导出网站源码。']
  },
  {
    id: 3,
    title: '用 DClaw 快速制作 PPT',
    description: '告别繁琐排版，一键生成结构清晰、设计精美的演示文稿。',
    icon: Presentation,
    videoUrl: 'https://www.dianclaw.cn/uploads/videos/PPT.mp4',
    steps: ['输入你的演讲主题或提供一份文本大纲。', '选择你喜欢的 PPT 模板风格。', 'DClaw 将自动为你生成多页包含图文排版的演示文稿。', '预览无误后，直接下载 PPTX 文件。']
  }
]

function useConnectorIndex(enableConnectorGuide) {
  const [connectorIndex, setConnectorIndex] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (!enableConnectorGuide) return
    let cancelled = false
    const run = async () => {
      setLoading(true)
      try {
        const res = await fetch('/connector-guides/index.json', { cache: 'no-cache' })
        if (!res.ok) {
          if (!cancelled) setConnectorIndex(null)
          return
        }
        const data = await res.json()
        if (!cancelled) setConnectorIndex(data)
      } finally {
        if (!cancelled) setLoaded(true)
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [enableConnectorGuide])

  return { connectorIndex, loading, loaded }
}

function GuideLayout({ enableConnectorGuide }) {
  const { connectorIndex, loading, loaded } = useConnectorIndex(enableConnectorGuide)
  const location = useLocation()
  const navigate = useNavigate()

  const defaultSlug = connectorIndex?.defaultSlug || connectorIndex?.groups?.[0]?.items?.[0]?.slug
  const [manualExpanded, setManualExpanded] = useState(true)
  const [manualGroupExpanded, setManualGroupExpanded] = useState(() => {
    const next = {}
    GUIDE_MANUAL_GROUPS.forEach((g) => {
      next[g.title] = true
    })
    return next
  })
  const [videosExpanded, setVideosExpanded] = useState(true)
  const [connectorsExpanded, setConnectorsExpanded] = useState(true)
  const [shrimpExpanded, setShrimpExpanded] = useState(true)
  const [groupExpanded, setGroupExpanded] = useState({})

  useEffect(() => {
    if (!enableConnectorGuide) return
    if (loading) return
    if (!connectorIndex?.groups?.length) return
    setGroupExpanded((prev) => {
      if (Object.keys(prev).length) return prev
      const next = {}
      connectorIndex.groups.forEach((g) => {
        next[g.title] = true
      })
      return next
    })
  }, [connectorIndex, enableConnectorGuide, loading])

  useEffect(() => {
    if (
      location.pathname.endsWith('/guide') ||
      location.pathname.endsWith('/guide/') ||
      location.pathname.endsWith('/guide/manual') ||
      location.pathname.endsWith('/guide/manual/')
    ) {
      navigate('/guide/manual/basic-flow', { replace: true })
    }
  }, [defaultSlug, enableConnectorGuide, loaded, loading, location.pathname, navigate])

  const isVideos = location.pathname.includes('/guide/videos')
  const isConnectors = location.pathname.includes('/guide/connectors')
  const isManual = location.pathname.includes('/guide/manual')
  const isShrimp = location.pathname.includes('/guide/shrimp')
  const activeVideoId = (() => {
    const m = location.pathname.match(/\/guide\/videos\/(\d+)/)
    if (m) return Number(m[1])
    if (location.pathname.endsWith('/guide/videos') || location.pathname.endsWith('/guide/videos/')) return 0
    return null
  })()
  const activeManualSlug = (() => {
    const m = location.pathname.match(/\/guide\/manual\/([^/?#]+)/)
    if (m) return m[1]
    return null
  })()
  const activeShrimpSlug = (() => {
    const m = location.pathname.match(/\/guide\/shrimp\/([^/?#]+)/)
    if (m) return m[1]
    return null
  })()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-12">
      <SEO title="新手指引" description="DClaw 新手指引与连接器配置指南。" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">新手指引</h1>
          <p className="mt-4 text-lg text-gray-600 dark:text-gray-400">如何养虾、使用手册、连接器配置指南与视频教程</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          <div className="lg:w-1/3">
            <div className="lg:sticky lg:top-24 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">导航</h2>
              </div>

              <div className="p-2">
                <div>
                  <button
                    type="button"
                    onClick={() => setShrimpExpanded((v) => !v)}
                    className="w-full px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                  >
                    {shrimpExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Fish className="w-4 h-4" />
                    如何养虾
                  </button>

                  {shrimpExpanded && (
                    <ul className="py-1">
                      {GUIDE_SHRIMP_PAGES.map((item) => {
                        const active = isShrimp && activeShrimpSlug === item.slug
                        return (
                          <li key={item.slug}>
                            <Link
                              to={`/guide/shrimp/${item.slug}`}
                              className={`block pl-10 pr-4 py-2 text-sm rounded-lg transition-colors ${
                                active
                                  ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              {item.title}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => setManualExpanded((v) => !v)}
                    className="w-full px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                  >
                    {manualExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <BookOpen className="w-4 h-4" />
                    使用手册
                  </button>

                  {manualExpanded && (
                    <div className="mt-1">
                      {GUIDE_MANUAL_GROUPS.map((group) => {
                        const expanded = manualGroupExpanded[group.title] !== false
                        return (
                          <div key={group.title}>
                            <button
                              type="button"
                              onClick={() => setManualGroupExpanded((prev) => ({ ...prev, [group.title]: !expanded }))}
                              className="w-full pl-10 pr-4 py-2 text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                            >
                              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                              <span className="truncate">{group.title}</span>
                            </button>

                            {expanded && (
                              <ul className="py-1">
                                {group.items.map((item) => {
                                  const active = isManual && activeManualSlug === item.slug
                                  const Icon = item.icon
                                  return (
                                    <li key={item.slug}>
                                      <Link
                                        to={`/guide/manual/${item.slug}`}
                                        className={`flex items-center gap-2 pl-16 pr-4 py-2 text-sm rounded-lg transition-colors ${
                                          active
                                            ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                                            : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                      >
                                        <Icon className="w-4 h-4 flex-shrink-0" />
                                        <span className="truncate">{item.title}</span>
                                      </Link>
                                    </li>
                                  )
                                })}
                              </ul>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <button
                    type="button"
                    onClick={() => setVideosExpanded((v) => !v)}
                    className="w-full px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                  >
                    {videosExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <Video className="w-4 h-4" />
                    视频教程列表
                  </button>

                  {videosExpanded && (
                    <ul className="py-1">
                      {VIDEO_TUTORIALS.map((item) => {
                        const active = activeVideoId === item.id
                        return (
                          <li key={item.id} className={item.id === 0 ? 'hidden' : ''}>
                            <Link
                              to={`/guide/videos/${item.id}`}
                              className={`block pl-10 pr-4 py-2 text-sm rounded-lg transition-colors ${
                                active
                                  ? 'bg-dclaw-100 dark:bg-dclaw-900/30 text-dclaw-700 dark:text-dclaw-300'
                                  : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                              }`}
                            >
                              {item.title}
                            </Link>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>

                {enableConnectorGuide && (
                  <div className="mt-3 hidden">
                    <button
                      type="button"
                      onClick={() => setConnectorsExpanded((v) => !v)}
                      className="w-full px-4 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                    >
                      {connectorsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      <BookOpen className="w-4 h-4" />
                      连接器配置指南
                    </button>

                    {connectorsExpanded && loading && (
                      <div className="px-10 py-2 text-sm text-gray-500 dark:text-gray-400">正在加载...</div>
                    )}

                    {connectorsExpanded && !loading && connectorIndex?.groups?.length > 0 && (
                      <div className="mt-1">
                        {connectorIndex.groups.map((group) => {
                          const expanded = groupExpanded[group.title] !== false
                          return (
                            <div key={group.title}>
                              <button
                                type="button"
                                onClick={() => setGroupExpanded((prev) => ({ ...prev, [group.title]: !expanded }))}
                                className="w-full pl-10 pr-4 py-2 text-sm text-gray-600 dark:text-gray-300 flex items-center gap-2 hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
                              >
                                {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                <span className="truncate">{group.title}</span>
                              </button>

                              {expanded && (
                                <ul className="py-1">
                                  {group.items.map((item) => {
                                    const active = isConnectors && location.pathname.endsWith(`/${item.slug}`)
                                    return (
                                      <li key={item.slug}>
                                        <Link
                                          to={`/guide/connectors/${item.slug}`}
                                          className={`block pl-16 pr-4 py-2 text-sm rounded-lg transition-colors ${
                                            active
                                              ? 'bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300'
                                              : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                          }`}
                                        >
                                          {item.title}
                                        </Link>
                                      </li>
                                    )
                                  })}
                                </ul>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>

          <div className="lg:w-2/3">
            <Outlet context={{ enableConnectorGuide, connectorIndex }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function ConnectorDoc() {
  const { slug } = useParams()
  const { enableConnectorGuide, connectorIndex } = useOutletContext()
  const navigate = useNavigate()

  const [html, setHtml] = useState('')
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  const defaultSlug = connectorIndex?.defaultSlug || connectorIndex?.groups?.[0]?.items?.[0]?.slug

  useEffect(() => {
    if (!enableConnectorGuide) {
      navigate('/guide/videos', { replace: true })
      return
    }
    if (!slug && defaultSlug) {
      navigate(`/guide/connectors/${defaultSlug}`, { replace: true })
    }
  }, [defaultSlug, enableConnectorGuide, navigate, slug])

  useEffect(() => {
    if (!slug) return
    let cancelled = false
    const run = async () => {
      setLoading(true)
      setNotFound(false)
      try {
        const res = await fetch(`/connector-guides/pages/${slug}.html`, { cache: 'no-cache' })
        if (!res.ok) {
          if (!cancelled) setNotFound(true)
          return
        }
        const text = await res.text()
        if (!cancelled) setHtml(text)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-gray-600 dark:text-gray-400">
        正在加载...
      </div>
    )
  }

  if (notFound) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden p-8 text-gray-600 dark:text-gray-400">
        内容不存在
      </div>
    )
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-8 connector-doc" dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  )
}

function VideoGuide() {
  const { videoId } = useParams()
  const tutorials = useMemo(() => VIDEO_TUTORIALS, [])
  const activeVideo = useMemo(() => {
    const n = Number(videoId)
    if (!Number.isFinite(n)) return 0
    if (n < 0 || n >= tutorials.length) return 0
    return n
  }, [tutorials.length, videoId])

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="p-8">
        <div className="rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
          <div className="aspect-video bg-gray-100 dark:bg-gray-900">
            <video key={tutorials[activeVideo].videoUrl} className="w-full h-full object-cover" controls autoPlay preload="metadata">
              <source src={tutorials[activeVideo].videoUrl} type="video/mp4" />
              您的浏览器不支持 HTML5 视频。
            </video>
          </div>

          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{tutorials[activeVideo].title}</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">{tutorials[activeVideo].description}</p>
            <div className="space-y-4">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ArrowRight className="w-5 h-5 text-blue-600" />
                操作步骤
              </h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {tutorials[activeVideo].steps.map((step, idx) => (
                  <div key={idx} className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center font-bold">{idx + 1}</div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 pt-1">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function Guide() {
  const enableConnectorGuide = import.meta.env.VITE_ENABLE_CONNECTOR_GUIDE !== 'false'

  return (
    <Routes>
      <Route element={<GuideLayout enableConnectorGuide={enableConnectorGuide} />}>
        <Route index element={<Navigate to="manual/basic-flow" replace />} />
        <Route path="manual/:slug" element={<GuideManual />} />
        <Route path="manual" element={<Navigate to="manual/basic-flow" replace />} />
        <Route path="shrimp/:slug" element={<GuideShrimp />} />
        <Route path="shrimp" element={<Navigate to="shrimp/first-meet" replace />} />
        <Route path="videos/:videoId" element={<VideoGuide />} />
        <Route path="videos" element={<VideoGuide />} />
        {enableConnectorGuide && <Route path="connectors/:slug" element={<ConnectorDoc />} />}
        {enableConnectorGuide && <Route path="connectors" element={<ConnectorDoc />} />}
        <Route path="*" element={<Navigate to="manual/basic-flow" replace />} />
      </Route>
    </Routes>
  )
}
